/* eslint-disable new-cap, @typescript-eslint/no-empty-function */
/**
 * Delivery API hooks for managing delivery emails
 * Calls Python API endpoints at strix-api /api/deliveries
 */
import { fetchAuthSession } from "@aws-amplify/auth";
import type {
  ActionCategory,
  CompanyState,
  DeliveryState,
  DotReaction,
  InterestLevel,
} from "constants/delivery-analytics";
import { useCallback, useEffect, useState } from "react";

const DISTRIBUTION_API_URL =
  process.env.REACT_APP_API_ENDPOINT?.replace(/\/graphql$/, "") || "";

// =====================================
// Types
// =====================================

export interface UserSummary {
  id: string;
  name: string;
  email: string;
}

/** 配信メール一覧アイテム */
export interface DeliveryItem {
  id: string;
  status: string;
  fromEmail: string;
  fromName: string | null;
  subject: string;
  groupName: string | null;
  /** 紐づく配信シリーズ ID. null = 単発配信 */
  seriesId: string | null;
  seriesName: string | null;
  totalRecipients: number;
  successCount: number;
  failureCount: number;
  includeSender: boolean;
  scheduledAt: string | null;
  sentAt: string | null;
  createdBy: UserSummary;
  createdAt: string;
}

/** 配信メール詳細 */
export interface DeliveryDetail extends DeliveryItem {
  bodyText: string;
  bodyHtml: string | null;
  groupId: string | null;
  updatedAt: string;
}

/** 配信メール一覧レスポンス */
export interface DeliveryListResponse {
  items: DeliveryItem[];
  total: number;
  page: number;
  limit: number;
}

/** 配信メール作成リクエスト */
export interface CreateDeliveryInput {
  groupId: string;
  /** 配信シリーズ ID. 単発配信は省略 or null. */
  seriesId?: string | null;
  fromEmail: string;
  fromName?: string;
  subject: string;
  bodyText: string;
  bodyHtml?: string;
  includeSender?: boolean;
  scheduledAt?: string;
}

/** 配信メール更新リクエスト */
export interface UpdateDeliveryInput {
  groupId?: string;
  seriesId?: string | null;
  fromEmail?: string;
  fromName?: string;
  subject?: string;
  bodyText?: string;
  bodyHtml?: string;
  includeSender?: boolean;
  scheduledAt?: string | null;
}

/** 予約配信リクエスト */
export interface ScheduleDeliveryInput {
  scheduledAt: string;
}

/** 配信実行レスポンス */
export interface SendDeliveryResponse {
  id: string;
  status: string;
  totalRecipients: number;
  message: string;
}

/** 宛先プレビューアイテム */
export interface RecipientPreviewItem {
  email: string;
  name: string | null;
  companyName: string | null;
  isActive: boolean;
}

/** 宛先プレビューレスポンス */
export interface RecipientPreviewResponse {
  recipients: RecipientPreviewItem[];
  total: number;
}

/** 配信先レスポンス */
export interface DeliveryRecipient {
  id: string;
  email: string;
  name: string | null;
  companyName: string | null;
  status: string;
  sentAt: string | null;
  errorMessage: string | null;
  /**
   * 状態列の 5 値 (pending / sent / undelivered / complained / failed)。
   * `pending` = まだ送信を試みていない宛先で、下書き・予約中の配信でだけ出る。
   */
  deliveryState: DeliveryState;
  /** 開封回数（回）。ボットは除外 */
  openCount: number;
  /** クリック回数（回）。ボットは除外 */
  clickCount: number;
  /** 返信があったか */
  replied: boolean;
  /** 最終反応 (open / click / reply のうち最も新しい時刻) */
  lastReactionAt: string | null;
  /** 不達の bounceType */
  bounceType: string | null;
  /** 不達の理由コード (bounceSubType 等) */
  detailType: string | null;
  /** 届かなかった宛先の 4 分類。届いている宛先は null */
  actionCategory: ActionCategory | null;
}

/** 宛先一覧の並び替え (A5) */
export type RecipientSort = "reaction" | "sent" | "name";

/** 宛先一覧の絞り込み (A5) */
export type RecipientFilter = "all" | "undelivered";

/** 配信先一覧レスポンス */
export interface RecipientListResponse {
  items: DeliveryRecipient[];
  total: number;
  page: number;
  limit: number;
  /** 計測対象の配信か（列の出し分けに使う） */
  measured: boolean;
  /** 実際に適用された並び順 (measured=false のとき reaction → name に縮退) */
  sort: RecipientSort;
}

// =====================================
// 分析 API (A1 / A2 / A4) の型
// =====================================

/** 時系列の 1 点（送信からの経過時間バケット。固定 28 点） */
export type TimelinePoint = {
  index: number;
  startMinutes: number;
  endMinutes: number;
  label: string;
  /**
   * 横軸に目盛ラベルを出す点か。
   *
   * どのバケットが目盛になるかはバックエンドの刻み幅の決め方に完全に従属する。
   * フロントが独立に書き写すと、刻みを変えたときに例外は出ず「目盛が黙って
   * 消える」だけになるため、判定はバケット定義と同じ場所（API 側）に置く。
   */
  tick: boolean;
  opens: number;
  clicks: number;
  replies: number;
};

/** リンク別クリック内訳 */
export type LinkBreakdownItem = {
  linkId: string;
  url: string;
  totalClicks: number;
  uniqueClicks: number;
};

/** デバイス別の開封内訳 */
export type DeviceBreakdownItem = {
  deviceType: string;
  recipients: number;
};

/** 不達チップの内訳 */
export type UndeliveredBreakdown = {
  total: number;
  bounced: number;
  rejected: number;
  failed: number;
  actionRequired: number;
  deleteCandidate: number;
  needsCheck: number;
  noAction: number;
  senderAction: number;
};

/** A1: 配信 1 件の分析 */
export type DeliveryAnalytics = {
  deliveryId: string;
  measured: boolean;
  includeBots: boolean;
  sent: number;
  undelivered: number;
  delivered: number;
  undeliveredBreakdown: UndeliveredBreakdown;
  uniqueOpens: number | null;
  totalOpens: number | null;
  uniqueClicks: number | null;
  totalClicks: number | null;
  replyRecipients: number;
  replyMessages: number;
  openRate: number | null;
  clickRate: number | null;
  replyRate: number | null;
  linkBreakdown: LinkBreakdownItem[];
  deviceBreakdown: DeviceBreakdownItem[];
  timeline: TimelinePoint[];
  excludedBotOpenRecipients: number;
  excludedBotClickRecipients: number;
};

/** A4: 4 状態それぞれの企業数（単位は社） */
export type CompanyActivityCounts = {
  total: number;
  denominator: number;
  engaged: number;
  lapsed: number;
  neverResponded: number;
  notDelivered: number;
};

/** A4: 推移ドット列の 1 区間 */
export type CompanyActivityDot = {
  index: number;
  reaction: DotReaction;
};

/** A4: 会社 1 社の状態 */
export type CompanyActivityItem = {
  companyId: string;
  companyName: string;
  state: CompanyState;
  interestLevel: InterestLevel | null;
  lastReactionAt: string | null;
  lastReactionType: string | null;
  lastSentAt: string | null;
  dots: CompanyActivityDot[];
};

/** A4: 直近のやりとり */
export type CompanyActivityResponse = {
  days: number;
  periodFrom: string;
  periodTo: string;
  counts: CompanyActivityCounts;
  items: CompanyActivityItem[];
  total: number;
  page: number;
  limit: number;
};

/** A4 の並び替えタブ */
export type CompanyActivitySort = "interest" | "lapsed" | "not_delivered";

// =====================================
// Fetch helper
// =====================================

/**
 * 認証トークンを取得する
 * @return {Promise<string | null>} トークン文字列またはnull
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch (_e) {
    return null;
  }
}

/**
 * 配信APIへのリクエストを実行する
 * @param {string} method HTTPメソッド
 * @param {string} path APIパス
 * @param {unknown} body リクエストボディ
 * @return {Promise<T>} レスポンスデータ
 */
async function fetchDeliveryApi<T>(
  method: "GET" | "POST" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const token = await getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const options: RequestInit = { method, headers };

  if (body) {
    options.body = JSON.stringify(body);
  }

  const res = await fetch(`${DISTRIBUTION_API_URL}${path}`, options);

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    throw new Error(
      (errorBody as { detail?: string }).detail ||
        `API error: ${res.status} ${res.statusText}`,
    );
  }

  if (res.status === 202 || res.status === 204) {
    if (res.status === 204) return {} as T;
  }

  return res.json();
}

// =====================================
// Delivery API Hooks
// =====================================

export interface UseDeliveriesApiOptions {
  status?: "draft" | "scheduled" | "history";
  /** 配信シリーズで絞り込み (任意) */
  seriesId?: string | null;
  page?: number;
  limit?: number;
}

export interface UseDeliveriesApiResult {
  data: DeliveryListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<DeliveryListResponse>;
}

/**
 * 配信メール一覧取得
 * @param {UseDeliveriesApiOptions} options オプション
 * @return {UseDeliveriesApiResult} 配信一覧データとローディング状態
 */
export function useDeliveriesApi(
  options: UseDeliveriesApiOptions = {},
): UseDeliveriesApiResult {
  const [data, setData] = useState<DeliveryListResponse | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchDeliveries =
    useCallback(async (): Promise<DeliveryListResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const params = new URLSearchParams();
        if (options.status) params.append("status", options.status);
        if (options.seriesId) params.append("seriesId", options.seriesId);
        if (options.page) params.append("page", String(options.page));
        if (options.limit) params.append("limit", String(options.limit));

        const queryString = params.toString();
        const path = `/api/deliveries${queryString ? `?${queryString}` : ""}`;

        const responseData = await fetchDeliveryApi<DeliveryListResponse>(
          "GET",
          path,
        );
        setData(responseData);
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    }, [options.status, options.seriesId, options.page, options.limit]);

  useEffect(() => {
    fetchDeliveries().catch(() => {});
  }, [fetchDeliveries]);

  return { data, error, loading, refetch: fetchDeliveries };
}

export interface UseDeliveryApiResult {
  data: DeliveryDetail | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<DeliveryDetail>;
}

/**
 * 配信メール詳細取得
 * @param {string} deliveryId 配信メールID
 * @return {UseDeliveryApiResult} 配信詳細データとローディング状態
 */
export function useDeliveryApi(deliveryId: string): UseDeliveryApiResult {
  const [data, setData] = useState<DeliveryDetail | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchDelivery = useCallback(async (): Promise<DeliveryDetail> => {
    setLoading(true);
    setError(undefined);

    try {
      const responseData = await fetchDeliveryApi<DeliveryDetail>(
        "GET",
        `/api/deliveries/${deliveryId}`,
      );
      setData(responseData);
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [deliveryId]);

  useEffect(() => {
    if (deliveryId) {
      fetchDelivery().catch(() => {});
    }
  }, [deliveryId, fetchDelivery]);

  return { data, error, loading, refetch: fetchDelivery };
}

/**
 * 配信メール作成
 * @return {object} 作成関数とローディング状態
 */
export function useCreateDeliveryApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const createDelivery = useCallback(
    async (input: CreateDeliveryInput): Promise<DeliveryDetail> => {
      setLoading(true);
      setError(undefined);

      try {
        return await fetchDeliveryApi<DeliveryDetail>(
          "POST",
          "/api/deliveries",
          input,
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createDelivery, loading, error };
}

/**
 * 配信メール更新
 * @return {object} 更新関数とローディング状態
 */
export function useUpdateDeliveryApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const updateDelivery = useCallback(
    async (id: string, input: UpdateDeliveryInput): Promise<DeliveryDetail> => {
      setLoading(true);
      setError(undefined);

      try {
        return await fetchDeliveryApi<DeliveryDetail>(
          "PUT",
          `/api/deliveries/${id}`,
          input,
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateDelivery, loading, error };
}

/**
 * 配信メール削除
 * @return {object} 削除関数とローディング状態
 */
export function useDeleteDeliveryApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const deleteDelivery = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(undefined);

    try {
      await fetchDeliveryApi<void>("DELETE", `/api/deliveries/${id}`);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteDelivery, loading, error };
}

/**
 * 即時配信実行
 * @return {object} 送信関数とローディング状態
 */
export function useSendDeliveryApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const sendDelivery = useCallback(
    async (id: string): Promise<SendDeliveryResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        return await fetchDeliveryApi<SendDeliveryResponse>(
          "POST",
          `/api/deliveries/${id}/send`,
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { sendDelivery, loading, error };
}

/**
 * 予約配信設定
 * @return {object} 予約関数とローディング状態
 */
export function useScheduleDeliveryApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const scheduleDelivery = useCallback(
    async (
      id: string,
      input: ScheduleDeliveryInput,
    ): Promise<DeliveryDetail> => {
      setLoading(true);
      setError(undefined);

      try {
        return await fetchDeliveryApi<DeliveryDetail>(
          "POST",
          `/api/deliveries/${id}/schedule`,
          input,
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { scheduleDelivery, loading, error };
}

/**
 * 予約キャンセル
 * @return {object} キャンセル関数とローディング状態
 */
export function useCancelDeliveryApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const cancelDelivery = useCallback(
    async (id: string): Promise<DeliveryDetail> => {
      setLoading(true);
      setError(undefined);

      try {
        return await fetchDeliveryApi<DeliveryDetail>(
          "POST",
          `/api/deliveries/${id}/cancel`,
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { cancelDelivery, loading, error };
}

/**
 * 宛先プレビュー
 * @return {object} プレビュー関数とローディング状態
 */
export function usePreviewRecipientsApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const previewRecipients = useCallback(
    async (groupId: string): Promise<RecipientPreviewResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        return await fetchDeliveryApi<RecipientPreviewResponse>(
          "POST",
          "/api/deliveries/preview-recipients",
          { groupId },
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { previewRecipients, loading, error };
}

export interface UseDeliveryRecipientsApiOptions {
  deliveryId: string;
  page?: number;
  limit?: number;
  /** 並び替え (既定は反応の強い順) */
  sort?: RecipientSort;
  /** 絞り込み (既定はすべて) */
  filter?: RecipientFilter;
}

/**
 * 宛先一覧（配信結果）
 * @param {UseDeliveryRecipientsApiOptions} options オプション
 * @return {object} 宛先一覧データとローディング状態
 */
export function useDeliveryRecipientsApi(
  options: UseDeliveryRecipientsApiOptions,
): {
  data: RecipientListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<RecipientListResponse>;
} {
  const [data, setData] = useState<RecipientListResponse | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchRecipients =
    useCallback(async (): Promise<RecipientListResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const params = new URLSearchParams();
        if (options.page) params.append("page", String(options.page));
        if (options.limit) params.append("limit", String(options.limit));
        if (options.sort) params.append("sort", options.sort);
        if (options.filter) params.append("filter", options.filter);

        const queryString = params.toString();
        const path = `/api/deliveries/${options.deliveryId}/recipients${queryString ? `?${queryString}` : ""}`;

        const responseData = await fetchDeliveryApi<RecipientListResponse>(
          "GET",
          path,
        );
        setData(responseData);
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    }, [
      options.deliveryId,
      options.page,
      options.limit,
      options.sort,
      options.filter,
    ]);

  useEffect(() => {
    if (options.deliveryId) {
      fetchRecipients().catch(() => {});
    }
  }, [options.deliveryId, fetchRecipients]);

  return { data, error, loading, refetch: fetchRecipients };
}

/**
 * 配信 1 件の分析 (A1)
 * @param {string} deliveryId 配信メール ID
 * @param {boolean} includeBots ボットの開封・クリックを含めるか
 * @return {object} 分析データとローディング状態
 */
export function useDeliveryAnalyticsApi(
  deliveryId: string,
  includeBots = false,
): {
  data: DeliveryAnalytics | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<DeliveryAnalytics>;
} {
  const [data, setData] = useState<DeliveryAnalytics | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async (): Promise<DeliveryAnalytics> => {
    setLoading(true);
    setError(undefined);

    try {
      const params = new URLSearchParams();
      if (includeBots) params.append("include_bots", "true");
      const queryString = params.toString();
      const responseData = await fetchDeliveryApi<DeliveryAnalytics>(
        "GET",
        `/api/deliveries/${deliveryId}/analytics${queryString ? `?${queryString}` : ""}`,
      );
      setData(responseData);
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [deliveryId, includeBots]);

  useEffect(() => {
    if (deliveryId) {
      fetchAnalytics().catch(() => undefined);
    }
  }, [deliveryId, fetchAnalytics]);

  return { data, error, loading, refetch: fetchAnalytics };
}

export interface UseCompanyActivityApiOptions {
  /** 対象期間 (30 / 90 / 180 日) */
  days?: number;
  sort?: CompanyActivitySort;
  page?: number;
  limit?: number;
}

/**
 * 直近のやりとり (A4)
 * @param {UseCompanyActivityApiOptions} options 期間・並び替え・ページング
 * @return {object} 会社の状態データとローディング状態
 */
export function useCompanyActivityApi(
  options: UseCompanyActivityApiOptions = {},
): {
  data: CompanyActivityResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<CompanyActivityResponse>;
} {
  const [data, setData] = useState<CompanyActivityResponse | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const { days, sort, page, limit } = options;

  const fetchActivity =
    useCallback(async (): Promise<CompanyActivityResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const params = new URLSearchParams();
        if (days) params.append("days", String(days));
        if (sort) params.append("sort", sort);
        if (page) params.append("page", String(page));
        if (limit) params.append("limit", String(limit));

        const queryString = params.toString();
        const responseData = await fetchDeliveryApi<CompanyActivityResponse>(
          "GET",
          `/api/deliveries/analytics/company-activity${queryString ? `?${queryString}` : ""}`,
        );
        setData(responseData);
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    }, [days, sort, page, limit]);

  useEffect(() => {
    fetchActivity().catch(() => undefined);
  }, [fetchActivity]);

  return { data, error, loading, refetch: fetchActivity };
}
